import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import { getCommunity } from 'helpers/community';
import { formatNumber } from 'helpers/generic';
import CounterPartsSelector from 'components/CounterPartsSelector';
import EmpoweredSelector from 'components/EmpoweredSelector';

const TableMovements = styled.table`
    font-size: 12px;
    
    thead {

        tr {

            th {
                font-weight: 600;
            }
        }
    }

    th, 
    td {
        &:nth-child(1) {
            width: 80px;
        }

        &:nth-child(3) {
            width: 100px;
            text-align: right;
        }
    }
`;

const Table = styled.table`
	font-size: 13px;

	thead {
		th {
			font-weight: 500;
		}
	}

	td, th {
		&:nth-child(2) {
			width: 100px;
			max-width: 100px;
			min-width: 100px;

			position: relative;
			text-align: right;

			input {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				border: 0;
				text-align: right;
				padding-right: 5px;
				padding-left: 5px;
			}
		}
		&:nth-child(3) {
			width: 40px;
			text-align: center;
		}
	}

	td {
		&:first-of-type {
			padding: 0;
		}

		&.has-errors {
			background: var(--bs-danger);
		}
	}
`;

let axiosCancelToken = null;

export default function AccountDelivery(props) {
    let hideModal = props.hideModal;
    let entity = props.movements.filter((item) => item.class.indexOf('BankMovement') !== -1)[0] ?? null; 

    let [loading, setLoading] = useState(false);
    let [properties, setProperties] = useState([]);

    useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();

		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

    const saveData = async () => {
        setLoading(true);
        await axios.post('/api/manager/bank-reconciliation/save', {
            type: 'account-delivery',
            community_id: getCommunity()?.id,
            bankmovement_id: entity.id,
            properties: properties.map(el => ({property_id: el.property_id, amount: el.amount}))
        }, {
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			toast.success('Movimientos conciliados');
			hideModal(true, true);
	  	}).catch((error) => {
			hideModal(true);

			if ( axios.isCancel(error) ) return;
			toast.error('Ha ocurrido un error al guardar');
		})
        setLoading(false);
    }

    const addItem = useCallback(() => {
		let newProperties = properties ? [...properties] : [];
		newProperties.push({
			property_id: null,
			amount: properties.length === 0 ? entity.amount : ''
		});
		setProperties(newProperties);
	}, [properties, entity.amount]);

	const removeItem = (idx) => {
		let newProperties = [...properties];
		newProperties.splice(idx, 1);
		setProperties(newProperties);
	}

    const setAmount = (idx, value) => {
		let newProperties = [...properties];
		newProperties[idx].amount = value;
		setProperties(newProperties);
	}

    const loadProperties = (input, callback, args) => {
		axios.get('/api/manager/properties/list', {
			params: {
				community_id: getCommunity()?.id,
				search: input,
				no_paginate: true,
			},
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			let formatted = [];
			response.data.forEach((property) => {
                if ( args.selectedIds.indexOf(property.id) !== -1 ) return;

				formatted.push({
                    value: property,
                    label: <div>
								{property?.name}
								{property?.owners?.length > 0 &&
									<div className="mt-1" style={{fontSize: '10px', color: '#5b9bd5'}}>{property.owners.map(o => o.name).join(', ')}</div>
								}
							</div>
                });
			});
			callback(formatted);
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});	
	}

    const selectProperty = (idx, value) => {
		let newProperties = [...properties];
		newProperties[idx].property = null;
		if ( value ) {
			newProperties[idx].property = {
				id: value?.id,
				name: value?.name
			};
		}
		newProperties[idx].property_id = value?.id;
        setProperties(newProperties);
	}

    useEffect(() => {
        if ( properties.length <= 0 ) {
            addItem();
        }
    }, [properties, addItem])

    let propertiesTotal = properties.reduce((carry, item) => carry += item.property?.id ? parseFloat(item.amount) : 0, 0);
    let hasInvalidAmounts = properties.some((item) => item.property?.id && !(parseFloat(item.amount) > 0));
    let negativeMovement = !(entity.amount > 0);
    let canReconciliate = propertiesTotal === entity.amount && !hasInvalidAmounts && !negativeMovement;

	return (
		<div className="row">
            <div className="col-md-12 mb-3">
                <label className="sbold">Movimiento bancario a conciliar</label>
                <TableMovements className="table table-sm table-bordered mb-0">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Concepto</th>
                            <th>Importe</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{moment(entity.date).format('DD-MM-YYYY')}</td>
                            <td>{entity.concept ?? ''}</td>
                            <td>{formatNumber(entity.amount ?? 0)}</td>
                        </tr>
                    </tbody>
                </TableMovements>
            </div>

            <div className="col-md-12">
                <label className="sbold">Propiedades a conciliar</label>
                <Table className="table table-sm table-bordered mb-0">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Importe</th>
                            <th>
                                <button className="btn-unstyled" onClick={() => addItem()}>
                                    <i className="bi bi-plus-circle-fill text-primary"></i>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        { properties && properties.map((el, idx) => {
                            return (
                                <tr key={idx}>
                                    <td 
                                        // className={(errors['counterparts.'+idx+'.estimategroup_id'] ? 'has-errors' : '')}
                                    >
                                        <EmpoweredSelector
                                            load={(input, callback, args) => loadProperties(input, callback, args)}
                                            args={{selectedIds: properties.map(el => el.property_id)}}
                                            onChange={(value) => selectProperty(idx, value)}
                                            timeout={250}
                                            label={el.property ? el.property?.name : ''}
                                            placeholder="Seleccionar"
                                            value={el.property?.id}
                                        />
                                    </td>
                                    <td 
                                        // className={(errors['counterparts.'+idx+'.amount'] ? 'has-errors' : '')}
                                    >
                                        <input 
                                            type="number" 
                                            className="no-arrows" 
                                            value={el.amount ?? ''} 
                                            onChange={(e) => setAmount(idx, e.target.value)}
                                            min={0.01}
                                            max={99999.99}
                                            disabled={!properties[idx].property?.id}
                                        />
                                    </td>
                                    <td>
                                        <button className={'btn-unstyled text-danger'} onClick={() => removeItem(idx)}><i className="bi bi-x-circle-fill"></i></button>
                                    </td>
                                </tr>
                            );
                        })}
                        { (!properties || !properties.length) && <tr><td colSpan="100%" className="p-2">No se han añadido propiedades</td></tr> }
                    </tbody>
                    { !canReconciliate &&
                        <tfoot>
                            <tr>
                                <td colSpan="100%" className="text-danger text-center">
                                    { negativeMovement
                                        ? 'No se pueden hacer entregas a cuenta de movimientos con importe negativo'
                                        : (hasInvalidAmounts
                                            ? 'El importe de cada propiedad tiene que ser mayor que 0'
                                            : 'El importe a conciliar tiene que ser similiar al importe del movimiento')
                                    }
                                </td>
                            </tr>
                        </tfoot>
                    }
                </Table>
            </div>
            
            <div className="col-md-12">
                <div className="text-end mt-3">
                    <button type="button" className="btn btn-primary text-white" disabled={!canReconciliate || loading} onClick={() => saveData()}>{loading ? 'cargando...' : 'Confirmar conciliación'}</button>
                </div>
            </div>
        </div>
	);
}


